import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchTrainerPage } from "@/api/trainerAuth";

// ============== ICONS DEFINITIONS ==============
const Icons = {
  ArrowLeft: () => <span>←</span>,
  ArrowRight: () => <span>→</span>,
  Star: () => <span>⭐</span>,
  Book: () => <span>📚</span>,
  Clock: () => <span>🕒</span>,
  Users: () => <span>👥</span>,
  Play: () => <span>▶</span>,
  Share: () => <span>↗</span>,
  Heart: () => <span>❤️</span>,
  Graduation: () => <span>🎓</span>,
  Calendar: () => <span>📅</span>,
  Trophy: () => <span>🏆</span>,
  Check: () => <span>✓</span>,
  Facebook: () => <span>f</span>,
  Twitter: () => <span>𝕏</span>,
  Instagram: () => <span>📷</span>,
  LinkedIn: () => <span>in</span>,
  Youtube: () => <span>▶️</span>,
  Home: () => <span>🏠</span>,
};

// ============== CSS STYLES ==============
const styles = `
    /* ============== CSS Variables ============== */
    :root {
      --primary: #6a11cb;
      --primary-light: #8a2be2;
      --primary-dark: #4a00e0;
      --secondary: #00dbde;
      --secondary-light: #00c6ff;
      --accent: #ff6b6b;
      --accent-light: #ff9e7d;
      --accent-dark: #ff4757;
      --dark: #1a1a2e;
      --dark-light: #16213e;
      --light: #f8f9fa;
      --light-gray: #e9ecef;
      --gray: #6c757d;
      --success: #00b894;
      --warning: #fdcb6e;
      --danger: #ff7675;
      --info: #00cec9;
      
      --gradient-primary: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      --gradient-secondary: linear-gradient(135deg, var(--secondary), var(--secondary-light), #36d1dc);
      --gradient-accent: linear-gradient(135deg, var(--accent), var(--accent-light), #ff9a9e);
      --gradient-dark: linear-gradient(135deg, var(--dark), var(--dark-light), #0f3460);
      
      --border-radius-xl: 25px;
      --border-radius-lg: 20px;
      --border-radius-md: 15px;
      --border-radius-sm: 10px;
      
      --box-shadow-3d: 15px 15px 30px rgba(0, 0, 0, 0.15), -5px -5px 20px rgba(255, 255, 255, 0.7);
      --box-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      --box-shadow-md: 0 20px 40px rgba(0, 0, 0, 0.1);
      --box-shadow-sm: 0 10px 30px rgba(0, 0, 0, 0.08);
      --box-shadow-hover: 0 35px 60px rgba(106, 17, 203, 0.15);
      --box-shadow-neon: 0 0 20px rgba(106, 17, 203, 0.7);
      
      --transition-smooth: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
      --transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      --transition-fast: all 0.3s ease;
      
      --glass-bg: rgba(255, 255, 255, 0.12);
      --glass-border: rgba(255, 255, 255, 0.25);
      --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
      
      --neon-primary: 0 0 20px rgba(106, 17, 203, 0.8);
      --neon-secondary: 0 0 20px rgba(0, 219, 222, 0.8);
      --neon-accent: 0 0 20px rgba(255, 107, 107, 0.8);
    }

    /* ============== Reset & Base ============== */
    .trainer-page {
      font-family: 'Cairo', 'Tajawal', 'Poppins', 'Segoe UI', sans-serif;
      line-height: 1.8;
      color: var(--dark);
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      direction: rtl;
      overflow-x: hidden;
      min-height: 100vh;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 30px;
    }

    section {
      padding: 80px 0;
      position: relative;
    }

    /* ============== Animations ============== */
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-15px); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }

    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: translateY(20px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes neonGlow {
      0%, 100% { 
        box-shadow: var(--neon-primary);
      }
      50% { 
        box-shadow: 0 0 30px rgba(106, 17, 203, 1);
      }
    }

    /* ============== Loading & Error ============== */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: var(--gradient-dark);
      position: relative;
      overflow: hidden;
    }

    .loading-container::before {
      content: '';
      position: absolute;
      width: 300%;
      height: 300%;
      background: 
        radial-gradient(circle, transparent 20%, rgba(106, 17, 203, 0.1) 21%, transparent 22%),
        radial-gradient(circle, transparent 20%, rgba(0, 219, 222, 0.1) 21%, transparent 22%);
      background-size: 150px 150px;
      animation: rotate 20s linear infinite;
    }

    .spinner {
      width: 80px;
      height: 80px;
      border: 4px solid transparent;
      border-radius: 50%;
      border-top: 4px solid var(--secondary);
      border-right: 4px solid var(--primary);
      border-bottom: 4px solid var(--accent);
      animation: rotate 1s linear infinite;
      margin-bottom: 30px;
      position: relative;
      z-index: 2;
      box-shadow: var(--box-shadow-neon);
    }

    .loading-container h2 {
      color: white;
      font-size: 1.8rem;
      font-weight: 700;
      position: relative;
      z-index: 2;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: pulse 2s ease-in-out infinite;
    }

    .error-container {
      text-align: center;
      padding: 100px 20px;
      background: var(--gradient-accent);
      color: white;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .error-container h2 {
      font-size: 3rem;
      margin-bottom: 30px;
      background: linear-gradient(45deg, white, #ff9a9e);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    /* ============== Header ============== */
    .trainer-header {
      background: var(--gradient-dark);
      color: white;
      padding: 140px 0 80px;
      position: relative;
      overflow: hidden;
    }

    .trainer-header::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 80%, rgba(106, 17, 203, 0.4) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 219, 222, 0.4) 0%, transparent 50%);
      animation: gradient 15s ease infinite;
      background-size: 400% 400%;
      filter: blur(40px);
      opacity: 0.5;
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 50px;
      position: relative;
      z-index: 2;
    }

    @media (max-width: 992px) {
      .header-content {
        flex-direction: column;
        text-align: center;
      }
    }

    .trainer-avatar {
      flex-shrink: 0;
      position: relative;
    }

    .avatar-container {
      width: 220px;
      height: 220px;
      border-radius: 50%;
      overflow: hidden;
      border: 5px solid var(--secondary);
      box-shadow: var(--neon-secondary), 0 0 40px rgba(0, 219, 222, 0.4);
      animation: float 6s ease-in-out infinite;
      background: white;
    }

    .avatar-container img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .trainer-info {
      flex: 1;
    }

    .trainer-badge {
      display: inline-block;
      background: var(--gradient-accent);
      color: white;
      padding: 8px 20px;
      border-radius: 30px;
      font-size: 0.9rem;
      font-weight: 700;
      margin-bottom: 20px;
      box-shadow: var(--neon-accent);
    }

    .trainer-name {
      font-size: 3.5rem;
      font-weight: 900;
      margin-bottom: 15px;
      background: linear-gradient(135deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.2;
    }

    .trainer-title {
      font-size: 1.4rem;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 25px;
      font-weight: 600;
    }

    .trainer-description {
      font-size: 1.2rem;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.8;
      margin-bottom: 30px;
      max-width: 800px;
    }

    .academy-link {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: var(--secondary);
      font-weight: 700;
      font-size: 1.1rem;
      text-decoration: none;
      padding: 12px 25px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: var(--border-radius-lg);
      transition: var(--transition-smooth);
      border: 2px solid transparent;
    }

    .academy-link:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: var(--secondary);
      transform: translateX(-10px);
    }

    .trainer-stats {
      display: flex;
      gap: 40px;
      margin-top: 40px;
      flex-wrap: wrap;
    }

    .stat-item {
      text-align: center;
    }

    .stat-number {
      font-size: 2.5rem;
      font-weight: 900;
      color: var(--secondary);
      line-height: 1;
      margin-bottom: 5px;
      text-shadow: 0 0 20px rgba(0, 219, 222, 0.5);
    }

    .stat-label {
      font-size: 1rem;
      color: rgba(255, 255, 255, 0.8);
      font-weight: 600;
    }

    /* ============== Buttons ============== */
    .btn {
      padding: 16px 36px;
      border-radius: var(--border-radius-xl);
      font-size: 1.1rem;
      font-weight: 700;
      cursor: pointer;
      transition: var(--transition-smooth);
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      text-decoration: none;
      position: relative;
      overflow: hidden;
    }

    .btn:hover {
      transform: translateY(-5px);
    }

    .btn-primary {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--box-shadow-3d), var(--neon-primary);
    }

    .btn-primary:hover {
      box-shadow: var(--box-shadow-hover), 0 0 40px rgba(106, 17, 203, 0.6);
    }

    .btn-secondary {
      background: var(--gradient-secondary);
      color: white;
      box-shadow: var(--box-shadow-3d), 0 10px 30px rgba(0, 219, 222, 0.4);
    }

    .btn-outline {
      background: transparent;
      color: white;
      border: 3px solid white;
      box-shadow: var(--box-shadow-sm);
    }

    .btn-outline:hover {
      background: white;
      color: var(--dark);
    }

    .btn-social {
      width: 50px;
      height: 50px;
      padding: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.2);
      transition: var(--transition-smooth);
    }

    .btn-social:hover {
      background: var(--gradient-primary);
      transform: translateY(-5px) rotate(10deg);
      border-color: transparent;
      box-shadow: var(--box-shadow-neon);
    }

    .btn-icon {
      width: 50px;
      height: 50px;
      padding: 0;
      border-radius: 50%;
      font-size: 1.2rem;
    }

    /* ============== Social Links ============== */
    .social-links {
      display: flex;
      gap: 15px;
      margin-top: 30px;
    }

    /* ============== Courses Section ============== */
    .courses-section {
      background: white;
      position: relative;
    }

    .section-header {
      text-align: center;
      margin-bottom: 60px;
      position: relative;
    }

    .section-title {
      font-size: 3rem;
      font-weight: 900;
      margin-bottom: 20px;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      position: relative;
      display: inline-block;
    }

    .section-title::after {
      content: '';
      position: absolute;
      bottom: -15px;
      right: 50%;
      transform: translateX(50%);
      width: 100px;
      height: 5px;
      background: var(--gradient-primary);
      border-radius: 5px;
      box-shadow: var(--neon-primary);
    }

    .section-subtitle {
      font-size: 1.3rem;
      color: var(--gray);
      max-width: 700px;
      margin: 25px auto 0;
      line-height: 1.8;
    }

    .courses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
      gap: 40px;
    }

    @media (max-width: 768px) {
      .courses-grid {
        grid-template-columns: 1fr;
      }
    }

    .course-card {
      background: white;
      border-radius: var(--border-radius-xl);
      overflow: hidden;
      box-shadow: var(--box-shadow-3d);
      transition: var(--transition-smooth);
      cursor: pointer;
      border: 3px solid transparent;
      position: relative;
    }

    .course-card:hover {
      transform: translateY(-20px) scale(1.02);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover), var(--box-shadow-3d);
    }

    .course-card:hover .course-image {
      transform: scale(1.1);
    }

    .course-badge {
      position: absolute;
      top: 20px;
      right: 20px;
      background: var(--gradient-accent);
      color: white;
      padding: 8px 20px;
      border-radius: 30px;
      font-size: 0.9rem;
      font-weight: 700;
      box-shadow: var(--neon-accent);
      z-index: 2;
    }

    .course-image {
      height: 220px;
      position: relative;
      overflow: hidden;
      background: linear-gradient(135deg, var(--primary), var(--secondary));
    }

    .course-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.8s ease;
    }

    .course-content {
      padding: 30px;
    }

    .course-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 20px;
      border-bottom: 2px solid var(--light-gray);
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--gray);
      font-weight: 600;
      font-size: 0.95rem;
    }

    .meta-item svg {
      color: var(--primary);
    }

    .course-title {
      font-size: 1.6rem;
      font-weight: 800;
      margin-bottom: 15px;
      color: var(--dark);
      line-height: 1.4;
    }

    .course-description {
      color: var(--gray);
      font-size: 1.1rem;
      line-height: 1.7;
      margin-bottom: 20px;
      height: 70px;
      overflow: hidden;
    }

    .course-level {
      display: inline-block;
      padding: 6px 15px;
      background: rgba(106, 17, 203, 0.1);
      color: var(--primary);
      border-radius: 20px;
      font-size: 0.9rem;
      font-weight: 700;
      margin-bottom: 20px;
    }

    .course-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
      padding-top: 20px;
      border-top: 2px solid var(--light-gray);
    }

    .course-price {
      font-size: 2rem;
      font-weight: 900;
      color: var(--primary-dark);
      position: relative;
    }

    .course-price::before {
      content: '$';
      position: absolute;
      top: 0;
      right: -20px;
      font-size: 1.2rem;
      color: var(--gray);
    }

    .course-actions {
      display: flex;
      gap: 15px;
    }

    /* ============== About Section ============== */
    .about-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      position: relative;
      overflow: hidden;
    }

    .about-content {
      max-width: 900px;
      margin: 0 auto;
    }

    .about-text {
      font-size: 1.2rem;
      line-height: 1.9;
      color: var(--dark);
      margin-bottom: 40px;
    }

    .skills-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 25px;
      margin-top: 40px;
    }

    .skill-item {
      background: white;
      padding: 25px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
    }

    .skill-item:hover {
      transform: translateY(-10px);
      box-shadow: var(--box-shadow-md);
    }

    .skill-name {
      font-size: 1.3rem;
      font-weight: 800;
      margin-bottom: 15px;
      color: var(--primary);
    }

    .skill-bar {
      height: 10px;
      background: var(--light-gray);
      border-radius: 5px;
      overflow: hidden;
      margin-top: 15px;
    }

    .skill-level {
      height: 100%;
      background: var(--gradient-primary);
      border-radius: 5px;
      transition: width 1.5s ease;
    }

    /* ============== Contact Section ============== */
    .contact-section {
      background: var(--gradient-dark);
      color: white;
      text-align: center;
      padding: 100px 0;
      position: relative;
    }

    .contact-section::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 30% 50%, rgba(106, 17, 203, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 70% 50%, rgba(0, 219, 222, 0.3) 0%, transparent 50%);
    }

    .contact-content {
      position: relative;
      z-index: 2;
      max-width: 800px;
      margin: 0 auto;
    }

    .contact-title {
      font-size: 2.5rem;
      font-weight: 900;
      margin-bottom: 30px;
      background: linear-gradient(135deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .contact-subtitle {
      font-size: 1.3rem;
      opacity: 0.9;
      margin-bottom: 40px;
      line-height: 1.8;
    }

    .contact-buttons {
      display: flex;
      gap: 20px;
      justify-content: center;
      flex-wrap: wrap;
    }

    /* ============== Footer ============== */
    .trainer-footer {
      background: var(--dark);
      color: white;
      padding: 60px 0 30px;
      text-align: center;
    }

    .back-home {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: white;
      text-decoration: none;
      font-size: 1.1rem;
      font-weight: 600;
      padding: 15px 30px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: var(--border-radius-lg);
      transition: var(--transition-smooth);
      margin-bottom: 40px;
    }

    .back-home:hover {
      background: var(--gradient-primary);
      transform: translateY(-5px);
    }

    .copyright {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.95rem;
      padding-top: 30px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* ============== Utilities ============== */
    .text-center { text-align: center; }
    .mt-20 { margin-top: 20px; }
    .mt-30 { margin-top: 30px; }
    .mt-40 { margin-top: 40px; }
    .mb-20 { margin-bottom: 20px; }
    .mb-30 { margin-bottom: 30px; }
    .mb-40 { margin-bottom: 40px; }
    .glass { background: var(--glass-bg); backdrop-filter: blur(10px); border: 1px solid var(--glass-border); }
    .shadow-lg { box-shadow: var(--box-shadow-lg); }
    .shadow-md { box-shadow: var(--box-shadow-md); }
    .shadow-sm { box-shadow: var(--box-shadow-sm); }
    .rounded-lg { border-radius: var(--border-radius-lg); }
    .rounded-xl { border-radius: var(--border-radius-xl); }
    .gradient-text { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `;

// ============== COMPONENT ==============
export default function TrainerPage() {
  const { trainerId } = useParams();
  const navigate = useNavigate();

  const [trainer, setTrainer] = useState(null);
  const [academy, setAcademy] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchTrainerPage(trainerId);
        setTrainer(data.trainer);
        setAcademy(data.academy);
        setCourses(data.courses || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [trainerId]);

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <style>{styles}</style>
        <div className="spinner"></div>
        <h2>جارٍ تحميل صفحة المدرّب...</h2>
      </div>
    );
  }

  // Error state
  if (!trainer) {
    return (
      <div className="error-container">
        <style>{styles}</style>
        <h2>المدرّب غير موجود</h2>
        <button 
          className="btn btn-primary"
          onClick={() => navigate(-1)}
          style={{ marginTop: '30px' }}
        >
          <Icons.ArrowLeft />
          <span>العودة للخلف</span>
        </button>
      </div>
    );
  }

  // Trainer stats
  const trainerStats = [
    { id: 1, number: courses.length, label: "دورة" },
    { id: 2, number: "1000+", label: "طالب" },
    { id: 3, number: "4.9", label: "تقييم" },
    { id: 4, number: "5+", label: "سنوات خبرة" }
  ];

  // Trainer skills (example)
  const trainerSkills = [
    { id: 1, name: "التدريب الاحترافي", level: 95 },
    { id: 2, name: "تطوير المناهج", level: 90 },
    { id: 3, name: "التواصل مع الطلاب", level: 98 },
    { id: 4, name: "التقنيات التعليمية", level: 85 }
  ];

  // Social links (example)
  const socialLinks = [
    { id: 1, platform: "Twitter", icon: <Icons.Twitter />, url: "#" },
    { id: 2, platform: "LinkedIn", icon: <Icons.LinkedIn />, url: "#" },
    { id: 3, platform: "YouTube", icon: <Icons.Youtube />, url: "#" },
    { id: 4, platform: "Instagram", icon: <Icons.Instagram />, url: "#" }
  ];

  return (
    <div className="trainer-page">
      <style>{styles}</style>

      {/* ================= TRAINER HEADER ================= */}
      <section className="trainer-header">
        <div className="container">
          <div className="header-content">
            {/* Trainer Avatar */}
            <div className="trainer-avatar">
              <div className="avatar-container">
                {trainer.image ? (
                  <img src={trainer.image} alt={trainer.name} />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--gradient-primary)',
                    color: 'white',
                    fontSize: '5rem'
                  }}>
                    {trainer.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Trainer Info */}
            <div className="trainer-info">
              <div className="trainer-badge">مدرب معتمد</div>
              
              <h1 className="trainer-name">{trainer.name}</h1>
              
              <p className="trainer-title">
                {trainer.title || "مدرب ومتخصص في مجال التعليم الإلكتروني"}
              </p>
              
              <p className="trainer-description">
                {trainer.description || 
                  "مدرب معتمد بخبرة تزيد عن 5 سنوات في مجال التعليم الإلكتروني. متخصص في تقديم الدورات التدريبية عالية الجودة مع التركيز على النتائج العملية والتطبيق المباشر."}
              </p>

              {/* Academy Link */}
              {academy && (
                <a 
                  href={`/academy/${academy.id}`} 
                  className="academy-link"
                >
                  <Icons.Graduation />
                  <span>الأكاديمية: {academy.name}</span>
                </a>
              )}

              {/* Stats */}
              <div className="trainer-stats">
                {trainerStats.map(stat => (
                  <div key={stat.id} className="stat-item">
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="social-links">
                {socialLinks.map(link => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-social"
                    title={link.platform}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COURSES SECTION ================= */}
      <section className="courses-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">دورات المدرّب</h2>
            <p className="section-subtitle">
              اكتشف مجموعة الدورات التدريبية التي يقدمها المدرّب {trainer.name}
            </p>
          </div>

          {courses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                background: 'var(--light-gray)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px',
                fontSize: '3rem'
              }}>
                <Icons.Book />
              </div>
              <h3 style={{ 
                color: 'var(--gray)', 
                fontSize: '1.5rem',
                marginBottom: '20px'
              }}>
                لا توجد دورات حالياً
              </h3>
              <p style={{ color: 'var(--gray)', maxWidth: '500px', margin: '0 auto' }}>
                قريباً سيتم إضافة دورات جديدة من قبل المدرّب
              </p>
            </div>
          ) : (
            <div className="courses-grid">
              {courses.map(course => (
                <div 
                  key={course.id}
                  className="course-card"
                  onClick={() => navigate(`/courses/${course.id}`)}
                >
                  {/* Course Badge */}
                  <div className="course-badge">
                    {course.level || "متوسط"}
                  </div>

                  {/* Course Image */}
                  <div className="course-image">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--gradient-primary)',
                        color: 'white',
                        fontSize: '3rem'
                      }}>
                        <Icons.Play />
                      </div>
                    )}
                  </div>

                  {/* Course Content */}
                  <div className="course-content">
                    {/* Course Meta */}
                    <div className="course-meta">
                      <div className="meta-item">
                        <Icons.Clock />
                        <span>{course.duration || "10 ساعات"}</span>
                      </div>
                      <div className="meta-item">
                        <Icons.Users />
                        <span>{course.students || "100+"} طالب</span>
                      </div>
                    </div>

                    {/* Course Title */}
                    <h3 className="course-title">{course.title}</h3>

                    {/* Course Description */}
                    <p className="course-description">
                      {course.description || "دورة تدريبية متخصصة تغطي المفاهيم الأساسية والمتقدمة في المجال."}
                    </p>

                    {/* Course Level */}
                    <div className="course-level">
                      {course.level || "جميع المستويات"}
                    </div>

                    {/* Course Footer */}
                    <div className="course-footer">
                      <div className="course-price">
                        {course.price || "49"}
                      </div>
                      
                      <div className="course-actions">
                        <button 
                          className="btn btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Add to favorites logic
                          }}
                          style={{ background: 'var(--light-gray)' }}
                        >
                          <Icons.Heart />
                        </button>
                        <button 
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${course.id}`);
                          }}
                        >
                          <span>التفاصيل</span>
                          <Icons.ArrowLeft />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ================= ABOUT SECTION ================= */}
      <section className="about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">عن المدرّب</h2>
            <p className="section-subtitle">
              المزيد من المعلومات حول خبرات ومهارات المدرّب {trainer.name}
            </p>
          </div>

          <div className="about-content">
            <div className="about-text">
              <p>
                {trainer.bio || 
                  `مدرس معتمد بخبرة عملية تزيد عن 5 سنوات في مجال التعليم الإلكتروني وتطوير المهارات. 
                  حاصل على عدة شهادات دولية في مجال التخصص، مع خبرة في تصميم وتطوير المناهج التدريبية 
                  التي تركز على الجانب العملي والتطبيقي.`}
              </p>
              
              <p style={{ marginTop: '20px' }}>
                {trainer.achievements || 
                  `ساعد أكثر من 1000 طالب في تحقيق أهدافهم التعليمية والمهنية. 
                  متخصص في تقديم المحتوى بطريقة تفاعلية وجذابة تضمن أقصى استفادة للمتعلمين.`}
              </p>
            </div>

            {/* Skills */}
            <div className="skills-list">
              {trainerSkills.map(skill => (
                <div key={skill.id} className="skill-item">
                  <h4 className="skill-name">{skill.name}</h4>
                  <div className="skill-bar">
                    <div 
                      className="skill-level" 
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginTop: '10px',
                    color: 'var(--gray)',
                    fontSize: '0.9rem'
                  }}>
                    <span>المستوى</span>
                    <span>{skill.level}%</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Certificates (Example) */}
            <div style={{ marginTop: '60px' }}>
              <h3 style={{ 
                fontSize: '1.8rem',
                fontWeight: '800',
                marginBottom: '30px',
                color: 'var(--dark)',
                textAlign: 'center'
              }}>
                <Icons.Trophy /> الشهادات والإنجازات
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                textAlign: 'center'
              }}>
                <div style={{ 
                  padding: '20px',
                  background: 'white',
                  borderRadius: 'var(--border-radius-lg)',
                  boxShadow: 'var(--box-shadow-sm)'
                }}>
                  <div style={{ 
                    fontSize: '2rem',
                    marginBottom: '15px',
                    color: 'var(--primary)'
                  }}>
                    🎓
                  </div>
                  <h4 style={{ marginBottom: '10px' }}>مدرب معتمد</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray)' }}>
                    شهادة تدريب دولية
                  </p>
                </div>
                
                <div style={{ 
                  padding: '20px',
                  background: 'white',
                  borderRadius: 'var(--border-radius-lg)',
                  boxShadow: 'var(--box-shadow-sm)'
                }}>
                  <div style={{ 
                    fontSize: '2rem',
                    marginBottom: '15px',
                    color: 'var(--success)'
                  }}>
                    📈
                  </div>
                  <h4 style={{ marginBottom: '10px' }}>أكثر من 1000 طالب</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray)' }}>
                    تدريب وتأهيل الطلاب
                  </p>
                </div>
                
                <div style={{ 
                  padding: '20px',
                  background: 'white',
                  borderRadius: 'var(--border-radius-lg)',
                  boxShadow: 'var(--box-shadow-sm)'
                }}>
                  <div style={{ 
                    fontSize: '2rem',
                    marginBottom: '15px',
                    color: 'var(--warning)'
                  }}>
                    ⭐
                  </div>
                  <h4 style={{ marginBottom: '10px' }}>تقييم 4.9/5</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray)' }}>
                    من طلاب الدورات
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT SECTION ================= */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-content">
            <h2 className="contact-title">تواصل مع {trainer.name}</h2>
            
            <p className="contact-subtitle">
              هل لديك استفسار أو ترغب في التعاون مع المدرّب؟
              تواصل معنا وسنكون سعداء بمساعدتك
            </p>
            
            <div className="contact-buttons">
              <button className="btn btn-primary">
                <span>إرسال رسالة</span>
                <Icons.ArrowLeft />
              </button>
              
              <button className="btn btn-outline">
                <span>جدولة اجتماع</span>
                <Icons.Calendar />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="trainer-footer">
        <div className="container">
          <a href="/" className="back-home">
            <Icons.Home />
            <span>العودة للرئيسية</span>
          </a>
          
          <div className="copyright">
            <p>© {new Date().getFullYear()} صفحة المدرب {trainer.name}. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}